/**
 * Telephonic Conversation Modal
 * =============================
 * Log an ad-hoc phone-call conversation directly against a lead. Creates a
 * completed LeadMeeting with type=telephonic_conversation and triggers the
 * same questionnaire-fill pipeline used for scheduled meetings.
 */

import React, { useState } from 'react';
import { leadAPI } from '../../api';
import { Modal, Button, Input } from '../ui';

const toDatetimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const TelephonicConversationModal = ({ isOpen, leadId, leadName, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: `Telephonic Conversation - ${leadName || ''}`,
    conversation_date: toDatetimeLocal(new Date()),
    duration_minutes: 15,
    participants: [],
    notes: '',
  });
  const [participantInput, setParticipantInput] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const resetState = () => {
    setFormData({
      title: `Telephonic Conversation - ${leadName || ''}`,
      conversation_date: toDatetimeLocal(new Date()),
      duration_minutes: 15,
      participants: [],
      notes: '',
    });
    setParticipantInput('');
    setErrors({});
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose && onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAddParticipant = () => {
    const email = participantInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !formData.participants.includes(email)) {
      setFormData((prev) => ({ ...prev, participants: [...prev.participants, email] }));
      setParticipantInput('');
    }
  };

  const handleRemoveParticipant = (email) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.filter((p) => p !== email),
    }));
  };

  const validate = () => {
    const next = {};
    if (!formData.notes || formData.notes.trim().length < 10) {
      next.notes = 'Notes must be at least 10 characters';
    }
    if (formData.duration_minutes < 1 || formData.duration_minutes > 600) {
      next.duration_minutes = 'Duration must be between 1 and 600 minutes';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        title: formData.title && formData.title.trim() ? formData.title.trim() : undefined,
        conversation_date: formData.conversation_date
          ? new Date(formData.conversation_date).toISOString()
          : undefined,
        duration_minutes: Number(formData.duration_minutes),
        participants: formData.participants.length > 0 ? formData.participants : undefined,
        notes: formData.notes.trim(),
      };
      const response = await leadAPI.recordTelephonicConversation(leadId, payload);
      const data = response?.data || response;
      resetState();
      onSuccess && onSuccess(data);
    } catch (err) {
      setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to save conversation' });
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button type="button" variant="primary" onClick={handleSubmit} disabled={loading}>
        {loading ? 'Saving...' : 'Save Conversation'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Record Telephonic Conversation"
      size="lg"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {errors.submit}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <Input
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder={`Telephonic Conversation - ${leadName || ''}`}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Conversation Date &amp; Time</label>
            <Input
              type="datetime-local"
              name="conversation_date"
              value={formData.conversation_date}
              onChange={handleChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
            <Input
              type="number"
              name="duration_minutes"
              value={formData.duration_minutes}
              onChange={handleChange}
              min="1"
              max="600"
              error={errors.duration_minutes}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Participants (optional)</label>
          <div className="flex gap-2 mb-2">
            <Input
              type="email"
              value={participantInput}
              onChange={(e) => setParticipantInput(e.target.value)}
              placeholder="participant@email.com"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddParticipant();
                }
              }}
            />
            <Button type="button" variant="outline" onClick={handleAddParticipant}>
              Add
            </Button>
          </div>
          {formData.participants.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.participants.map((email) => (
                <div
                  key={email}
                  className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                >
                  <span>{email}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveParticipant(email)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conversation Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={8}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
            placeholder="Paste your call notes or a summary of the conversation — key discussion points, requirements mentioned, next steps, etc. The system will use these to update the questionnaire."
          />
          {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes}</p>}
          <p className="mt-1 text-xs text-gray-500">
            Minimum 10 characters. These notes will be processed by AI to fill the discovery questionnaire.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default TelephonicConversationModal;
